//----------------------------------------------------------------------------------------
// This component is made to enforce numeric inputs be within their required range.
// It will take the input and clamp it between the min/max. If no max was provided then
// it assumes there is no upper range. If no min was provided it assumes zero is the min.
// If step is provided it will enforce it, so if step is 1 it will increment or decrement
// by 1 and if step is 0.1 it will increment or decrement by that instead. If no decimal
// is provided it assumes the number must be a whole number and if a decimal is provided
// it will return the value rounded to that number of decimals. The component can be
// disabled by the component holding it if that component determines it should not change.
//----------------------------------------------------------------------------------------
// Also, anything passed in the className prop will be appended to the existing className
//----------------------------------------------------------------------------------------
// Sample usage can be found on the CostQuote.js or ManageQuoteDefaults.js components
//----------------------------------------------------------------------------------------
import { Input, Label } from 'reactstrap';

const handleFocus = (event) => event.target.select();

function snapToStep(val, min, stepSize) {
   if (!stepSize || stepSize <= 0) return val;
   const snapped = Math.round((val - min) / stepSize) * stepSize + min;
   return snapped;
}

function normalizeInput(rawValue, decimals = 0, min = 0, stepSize = 1, max = null) {
   if (rawValue === "" || rawValue === null || rawValue === undefined)
      return min;

   let next = Number(rawValue);
   if (Number.isNaN(next)) next = min;

   // Clamp to valid minimum value
   next = Math.max(min, next);

   // Clamp to valid maximum value if a max was provided
   if (max !== null)
      next = Math.min(max, next);

   // Snap to closest step increment
   if (stepSize > 0)
      next = snapToStep(next, min, stepSize);

   // Fix floating precision issues
   next = Number(next.toFixed(decimals));

   return next;
}

export default function NumberField({
   label,
   value,
   onChange,
   min = 0,
   max = null,
   step = 0.01,
   decimals = 0,
   placeholder = "0",
   disabled = false,
   className = ""
}) {
   return (
      <div>
         {label ? <Label className="mb-1">{label}</Label> : null}
         <Input
            className={`w-36 md:w-44 lg:w-52 ${className}`.trim()}
            type="number"
            min={min}
            max={max ?? undefined}
            step={step}
            placeholder={placeholder}
            value={Number.isFinite(value) ? value : 0}
            onFocus={handleFocus}
            disabled={disabled}
            onChange={(e) => {
               if (e.target.value === "") {
                  onChange(min);
                  return;
               }
               // If we normalize here then if a user types a . it will remove it and round it
               // if they typed an invalid step increment it would immediately change it
               onChange(Number(e.target.value));
            }}
            onBlur={(e) => {
               // Normalizing on the blur will allow user free typing before fixing
               onChange(normalizeInput(Number(e.target.value), decimals, min, step, max));
            }}
         />
      </div>
   );
}
